import { useState, useEffect } from "react";
import { useLocation, useNavigate, Outlet } from "react-router-dom";
import axios from "axios";
import {
  Inbox, Star, Send, FileText, Trash2, Archive, Plus,
  Users, Calendar, CheckSquare, Settings, Bot, PenSquare,
  ChevronRight, Search, Mail,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import ComposeSheet from "@/components/mail/ComposeSheet";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const FOLDERS = [
  { id: "inbox", label: "Inbox", icon: Inbox },
  { id: "starred", label: "Starred", icon: Star },
  { id: "sent", label: "Sent", icon: Send },
  { id: "drafts", label: "Drafts", icon: FileText },
  { id: "archive", label: "Archive", icon: Archive },
  { id: "trash", label: "Trash", icon: Trash2 },
];

const NAV_ITEMS = [
  { id: "contacts", label: "Contacts", icon: Users, path: "/comms-centre/mail/contacts" },
  { id: "calendar", label: "Calendar", icon: Calendar, path: "/comms-centre/mail/calendar" },
  { id: "tasks", label: "Tasks", icon: CheckSquare, path: "/comms-centre/mail/tasks" },
  { id: "agent", label: "Agent Mail", icon: Bot, path: "/comms-centre/mail/agent" },
  { id: "settings", label: "Settings", icon: Settings, path: "/comms-centre/mail/settings" },
];

export default function MailLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [counts, setCounts] = useState({});
  const [composeOpen, setComposeOpen] = useState(false);

  const currentPath = location.pathname;
  const currentFolder = FOLDERS.find((f) => currentPath.endsWith(f.id))?.id || "inbox";

  useEffect(() => {
    fetchCounts();
  }, []);

  const fetchCounts = async () => {
    try {
      const res = await axios.get(`${API}/mail/counts`, {
        headers: getHeaders(), withCredentials: true,
      });
      setCounts(res.data.counts || {});
    } catch {}
  };

  const navigateToFolder = (folderId) => {
    navigate(`/comms-centre/mail/${folderId}`);
  };

  const unreadCount = counts[currentFolder]?.unread || 0;
  const totalCount = counts[currentFolder]?.total || 0;

  return (
    <div className="flex h-[calc(100vh-0px)]">
      {/* Mail Sidebar */}
      <aside className="w-56 bg-app-card border-r border-app flex flex-col">
        {/* Header */}
        <div className="p-3 border-b border-app">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Mail className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="font-['Barlow_Condensed'] text-base font-bold text-app tracking-wide">
                Smart Mail
              </h2>
              {unreadCount > 0 && (
                <p className="text-[10px] text-violet-500 font-medium">
                  {unreadCount} unread
                </p>
              )}
            </div>
          </div>

          {/* Compose Button */}
          <Sheet open={composeOpen} onOpenChange={setComposeOpen}>
            <SheetTrigger asChild>
              <Button className="w-full h-8 text-xs rounded-none bg-violet-600 hover:bg-violet-500 gap-1.5">
                <PenSquare className="w-3.5 h-3.5" /> Compose
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[600px] sm:max-w-[600px] p-0">
              <ComposeSheet onClose={() => setComposeOpen(false)} onSent={() => { fetchCounts(); setComposeOpen(false); }} />
            </SheetContent>
          </Sheet>
        </div>

        {/* Search */}
        <div className="px-3 py-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-app-secondary" />
            <input
              type="text"
              placeholder="Search mail..."
              className="w-full pl-7 pr-2 py-1.5 text-xs border border-app rounded focus:outline-none focus:border-violet-400 bg-app"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  navigate(`/comms-centre/mail/inbox?search=${encodeURIComponent(e.target.value)}`);
                }
              }}
            />
          </div>
        </div>

        {/* Folders */}
        <nav className="flex-1 px-2 py-1 space-y-0.5">
          <p className="text-[10px] font-semibold text-app-secondary uppercase px-2 py-1">Folders</p>
          {FOLDERS.map((folder) => {
            const Icon = folder.icon;
            const isActive = currentFolder === folder.id;
            const count = counts[folder.id];

            return (
              <button
                key={folder.id}
                onClick={() => navigateToFolder(folder.id)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded transition-colors ${
                  isActive
                    ? "bg-violet-800/40 text-violet-300 font-medium"
                    : "text-app hover:bg-app hover:text-app"
                }`}
              >
                <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="flex-1 text-left truncate">{folder.label}</span>
                {count?.unread > 0 && (
                  <Badge className="text-[10px] px-1.5 py-0 rounded-full bg-violet-100 text-violet-700 border-none">
                    {count.unread}
                  </Badge>
                )}
              </button>
            );
          })}
        </nav>

        {/* Separator */}
        <div className="border-t border-app" />

        {/* Nav Items */}
        <nav className="px-2 py-2 space-y-0.5">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = currentPath.startsWith(item.path);

            return (
              <button
                key={item.id}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded transition-colors ${
                  isActive
                    ? "bg-violet-800/40 text-violet-300 font-medium"
                    : "text-app hover:bg-app hover:text-app"
                }`}
              >
                <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="text-left truncate">{item.label}</span>
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto bg-app">
        <Outlet context={{ fetchCounts }} />
      </main>
    </div>
  );
}
